// medplum.js — the ONE Medplum session for the cost console. The super-admin logs
// in with email/password, scoped to the dedicated `healther-ops` Medplum project
// (NOT any hospital project). Mirrors opd-app/src/lib/medplum-client.js's
// project-bound login (startLogin with projectId + `offline_access` scope, then
// processCode), minus the OPD-specific role/department derivation — this console
// only needs a live session against the ops project.
//
// Runtime config comes from /ops-config.json ({ baseUrl, projectId }). There is NO
// super-admin credential anywhere in this bundle — the human types it into the
// login screen and the session persists to localStorage under 'ops.medplum.*'.
import { MedplumClient } from '@medplum/core';

const STORAGE_PREFIX = 'ops.medplum.';

// Namespaced localStorage so this console's session never collides with anything
// else on the same origin (MedplumClient defaults to one fixed key otherwise).
class PrefixedStorage {
  constructor(prefix) { this.prefix = prefix; }
  getString(key) { return window.localStorage.getItem(this.prefix + key) ?? undefined; }
  setString(key, value) {
    if (value === undefined) window.localStorage.removeItem(this.prefix + key);
    else window.localStorage.setItem(this.prefix + key, value);
  }
  getObject(key) { const s = this.getString(key); return s === undefined ? undefined : JSON.parse(s); }
  setObject(key, value) { this.setString(key, value === undefined ? undefined : JSON.stringify(value)); }
  clear() {
    for (const k of Object.keys(window.localStorage)) {
      if (k.startsWith(this.prefix)) window.localStorage.removeItem(k);
    }
  }
}

let configPromise = null;
export function loadOpsConfig() {
  if (!configPromise) {
    configPromise = fetch('/ops-config.json', { cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error('ops-config.json missing (' + res.status + ')');
      return res.json();
    });
  }
  return configPromise;
}

let clientPromise = null;
export function getClient() {
  if (!clientPromise) {
    clientPromise = loadOpsConfig().then((cfg) => {
      if (!cfg.baseUrl) throw new Error('ops-config.json is missing baseUrl');
      return new MedplumClient({
        baseUrl: cfg.baseUrl,
        storage: new PrefixedStorage(STORAGE_PREFIX),
      });
    });
  }
  return clientPromise;
}

/**
 * Project-bound super-admin login. `projectId` MUST be the healther-ops project
 * (fail loud when unset — a blank projectId in ops-config.json is a config error,
 * exactly the fail-loud rule opd-app enforces). `offline_access` makes Medplum
 * issue a refresh token so the session survives the access-token lifetime.
 */
export async function loginOps({ email, password }) {
  const cfg = await loadOpsConfig();
  const projectId = typeof cfg.projectId === 'string' ? cfg.projectId.trim() : '';
  if (!projectId) {
    throw new Error('ops-config.json is missing projectId (the healther-ops project id)');
  }
  const medplum = await getClient();
  const login = await medplum.startLogin({ email, password, projectId, scope: 'openid offline_access' });
  if (!login.code) {
    throw new Error(login.memberships
      ? 'This account belongs to multiple Medplum projects — pick a single ops project'
      : 'Medplum login did not return an authorization code');
  }
  await medplum.processCode(login.code);
  return medplum;
}

// Resolve the live session's profile, or null if there is none. Awaits the
// async /auth/me resume the constructor kicks off from persisted storage.
export async function currentProfile() {
  const medplum = await getClient();
  try {
    return (await medplum.getProfileAsync()) || null;
  } catch (_) {
    return null;
  }
}

export async function signOutOps() {
  const medplum = await getClient();
  try { await medplum.signOut(); } catch (_) { /* offline / already dead — local clear below is authoritative */ }
  medplum.clear();
  for (const k of Object.keys(window.localStorage)) {
    if (k.startsWith(STORAGE_PREFIX)) window.localStorage.removeItem(k);
  }
}
